import { createApi } from '@reduxjs/toolkit/query/react';
import camelize from 'camelize';
import {
  newToastNotification,
  ToastType,
} from '../../dashboard/components/ToastNotifications/toasts';
import { camelToSnake, snakeString } from '../../dashboard/utils';
import joinArray from '../../utils/joinArray';
import { createMutationErrorHandler } from '../utils/errorHandling';
import { baseQueryWithToast } from './base';

const CANIX_API_KEY_URL = 'api/vendor/:vendorId/integrations/canix/api_key/';
const CANIX_FACILITIES_URL =
  'api/vendor/:vendorId/integrations/canix/facilities/';
const CANIX_FILTER_SELLABLE_URL =
  'api/vendor/:vendorId/integrations/canix/:canixBatchId/filter_sellable/';
const CANIX_IMPORT_HISTORY_URL =
  'api/vendor/:vendorId/integrations/canix/batches/';
const CANIX_LOCATIONS_URL =
  'api/vendor/:vendorId/integrations/canix/:facilityId/locations/';
const CANIX_MAPPED_LOCATIONS_URL =
  'api/vendor/:vendorId/integrations/canix/map_facilities/';
const CANIX_START_IMPORT_URL = 'api/vendor/:vendorId/integrations/canix/start';

export const PLAID_URL =
  'api/:organizationType/:organizationTypeId/integrations/plaid/';
const PLAID_LINK_TOKEN_URL = `${PLAID_URL}link_token/`;
const PLAID_LOCATIONS_URL = `${PLAID_URL}locations/`;

// trust the actual url path instead of prepending it because ugh
const actualPathUrl = (url) => `${window.location.origin}${url}`;

export const integrationsApi = createApi({
  reducerPath: 'integrations',
  baseQuery: baseQueryWithToast,
  endpoints: (builder) => ({
    acceptLeafLinkTOS: builder.mutation({
      invalidatesTags: ['UserContext'],
      query: ({ organizationId }) => ({
        body: {
          terms_of_service: {
            direct_pay: new Date().toISOString(),
          },
        },
        method: 'PUT',
        url: `/api/terms-of-service/organizations/${organizationId}/`,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { errors = [] } = data;
          if (errors.length) {
            newToastNotification({
              body: joinArray(errors),
              toastType: ToastType.ERROR,
            });
          }
        } catch {
          /* */
        }
      },
    }),
    createPlaidAccessToken: builder.mutation({
      invalidatesTags: ['PlaidAccessTokens'],
      query: ({
        organizationType,
        organizationTypeId,
        plaidLinkData,
        publicToken,
      }) => ({
        body: { ...plaidLinkData, public_token: publicToken },
        method: 'POST',
        url: PLAID_URL.replace(':organizationType', organizationType).replace(
          ':organizationTypeId',
          organizationTypeId
        ),
      }),
      onQueryStarted: createMutationErrorHandler(
        'Failed to create Plaid access token'
      ),
    }),
    createPlaidLinkToken: builder.mutation({
      query: ({ organizationType, organizationTypeId }) => ({
        method: 'POST',
        url: PLAID_LINK_TOKEN_URL.replace(
          ':organizationType',
          organizationType
        ).replace(':organizationTypeId', organizationTypeId),
      }),
      onQueryStarted: createMutationErrorHandler(
        'Failed to create Plaid link token'
      ),
      transformResponse: camelize,
    }),

    relinkPlaidAccessToken: builder.mutation({
      invalidatesTags: ['PlaidAccessTokens'],
      query: ({ organizationType, organizationTypeId, tokenId }) => ({
        method: 'POST',
        url: `${PLAID_URL.replace(':organizationType', organizationType).replace(':organizationTypeId', organizationTypeId)}${tokenId}/relink/`,
      }),
      onQueryStarted: createMutationErrorHandler(
        'Failed to re-authenticate Plaid account'
      ),
      transformResponse: camelize,
    }),

    verifyPlaidAccessToken: builder.mutation({
      invalidatesTags: ['PlaidAccessTokens'],
      query: ({ organizationType, organizationTypeId, tokenId }) => ({
        method: 'POST',
        url: `${PLAID_URL.replace(':organizationType', organizationType).replace(':organizationTypeId', organizationTypeId)}${tokenId}/verify/`,
      }),
      onQueryStarted: createMutationErrorHandler(
        'Failed to verify Plaid account'
      ),
      transformResponse: camelize,
    }),

    filterCanixSellable: builder.mutation({
      query: ({ canixBatchId, vendorId }) => ({
        url: CANIX_FILTER_SELLABLE_URL.replace(
          ':canixBatchId',
          canixBatchId
        ).replace(':vendorId', vendorId),
        method: 'POST',
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { errors = [] } = data;

          if (errors.length) {
            newToastNotification({
              body: joinArray(errors),
              toastType: ToastType.ERROR,
            });
          } else {
            newToastNotification({
              body: 'Filtering sellable!',
              toastType: ToastType.SUCCESS,
            });
          }
        } catch {
          /* */
        }
      },
    }),
    getCanixApiKey: builder.query({
      query: ({ vendorId }) => CANIX_API_KEY_URL.replace(':vendorId', vendorId),
      transformResponse: camelize,
    }),

    getCanixBatches: builder.query({
      providesTags: ['CanixBatches'],
      query: ({ filters, ordering, page, pageSize, vendorId }) => ({
        method: 'GET',
        params: {
          ...camelToSnake(filters),
          page,
          page_size: pageSize,
          ...(() => {
            if (!ordering?.length) {
              return {};
            }
            const { desc, id } = ordering[0];
            return {
              sort_by: snakeString(id),
              sort_order: desc ? 'desc' : 'asc',
            };
          })(),
        },
        url: CANIX_IMPORT_HISTORY_URL.replace(':vendorId', vendorId),
      }),
      transformResponse: camelize,
    }),

    getCanixFacilities: builder.query({
      query: ({ vendorId }) =>
        CANIX_FACILITIES_URL.replace(':vendorId', vendorId),
      transformResponse: camelize,
    }),

    getCanixLocations: builder.query({
      query: ({ facilityId, vendorId }) =>
        CANIX_LOCATIONS_URL.replace(':facilityId', facilityId).replace(
          ':vendorId',
          vendorId
        ),
      transformResponse: camelize,
    }),

    getCanixMappedLocations: builder.query({
      query: ({ vendorId }) =>
        CANIX_MAPPED_LOCATIONS_URL.replace(':vendorId', vendorId),
      transformResponse: camelize,
    }),

    getImportResults: builder.query({
      query: ({ vendorId, importId }) => ({
        url: `/vendor/${vendorId}/integrations/${importId}/view-import-results`,
        method: 'POST',
      }),
      transformResponse: (response) => {
        const sortByName = (a, b) => {
          const nameA = a.existingStrain
            ? a.existingStrain.name
            : a.name || a.productName || '';
          const nameB = b.existingStrain
            ? b.existingStrain.name
            : b.name || b.productName || '';
          return nameA.localeCompare(nameB);
        };

        // Sort strains
        response.newStrains.sort(sortByName);
        response.updatedStrains.sort(sortByName);

        // Sort products
        response.newProducts.sort(sortByName);
        response.updatedProducts.sort(sortByName);

        // Sort prepacks
        response.newPrePacks.sort(sortByName);
        response.updatedPrePacks.sort(sortByName);

        // Sort weight based
        response.newBuckets.sort(sortByName);
        response.updatedBuckets.sort(sortByName);

        return response;
      },
    }),

    getPlaidAccessTokens: builder.query({
      providesTags: ['PlaidAccessTokens'],
      query: ({
        id,
        organizationType,
        organizationTypeId,
        page,
        pageSize,
      }) => ({
        method: 'GET',
        params: {
          'id[]': id,
          page,
          page_size: pageSize,
        },
        url: PLAID_URL.replace(':organizationType', organizationType).replace(
          ':organizationTypeId',
          organizationTypeId
        ),
      }),
      transformResponse: camelize,
    }),

    getPlaidLocations: builder.query({
      providesTags: ['PlaidLocations'],
      query: ({
        ordering,
        organizationType,
        organizationTypeId,
        page,
        pageSize,
      }) => ({
        method: 'GET',
        params: {
          page,
          page_size: pageSize,
          ...(() => {
            if (!ordering?.length) {
              return {};
            }
            const { desc, id } = ordering[0];
            return {
              sort_by: snakeString(id),
              sort_order: desc ? 'desc' : 'asc',
            };
          })(),
        },
        url: PLAID_LOCATIONS_URL.replace(
          ':organizationType',
          organizationType
        ).replace(':organizationTypeId', organizationTypeId),
      }),
      transformResponse: camelize,
    }),

    getResources: builder.query({
      query: ({ url }) => ({
        method: 'GET',
        url: actualPathUrl(url),
      }),
      transformResponse: camelize,
    }),

    getUserContext: builder.query({
      query: ({ baseUrl, userId }) => ({
        url: `/${baseUrl}/${userId}/context/api/`,
        method: 'GET',
      }),
      transformResponse: camelize,
      providesTags: ['UserContext'],
    }),

    mapCanixLocation: builder.mutation({
      query: ({
        active,
        facilityId,
        lastImport,
        license,
        roomId,
        stockLocationId,
        updateExisting,
        vendorId,
      }) => ({
        url: CANIX_MAPPED_LOCATIONS_URL.replace(':vendorId', vendorId),
        method: 'POST',
        body: {
          active,
          facility_id: facilityId,
          last_import: lastImport,
          license,
          room_id: roomId,
          stock_location_id: stockLocationId,
          update_existing: updateExisting,
        },
      }),
      onQueryStarted: async ({ vendorId }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { locationMapping, errors = [] } = camelize(data);

          if (errors.length) {
            newToastNotification({
              body: joinArray(errors),
              toastType: ToastType.ERROR,
            });
          } else {
            newToastNotification({
              body: 'Facility is successfully mapped!',
              toastType: ToastType.SUCCESS,
            });
            dispatch(
              integrationsApi.util.updateQueryData(
                'getCanixMappedLocations',
                { vendorId },
                (draft) => Object.assign(draft, { locationMapping })
              )
            );
          }
        } catch {
          /* */
        }
      },
    }),

    saveCanixApiKey: builder.mutation({
      query: ({ apiKey, vendorId }) => ({
        url: CANIX_API_KEY_URL.replace(':vendorId', vendorId),
        method: 'POST',
        body: { api_key: apiKey },
      }),
      onQueryStarted: async ({ vendorId }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { api_key: apiKey, errors = [] } = data;

          if (errors.length) {
            newToastNotification({
              body: joinArray(errors),
              toastType: ToastType.ERROR,
            });
          } else {
            newToastNotification({
              body: 'API Key is successfully updated!',
              toastType: ToastType.SUCCESS,
            });
            dispatch(
              integrationsApi.util.updateQueryData(
                'getCanixApiKey',
                { vendorId },
                (draft) => Object.assign(draft, { apiKey })
              )
            );
          }
        } catch {
          /* */
        }
      },
    }),

    startCanixImport: builder.mutation({
      invalidatesTags: ['CanixBatches'],
      query: ({ locationIds, vendorId }) => ({
        url: CANIX_START_IMPORT_URL.replace(':vendorId', vendorId),
        method: 'POST',
        body: { location_ids: locationIds },
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { errors = [] } = camelize(data);

          if (errors.length) {
            newToastNotification({
              body: joinArray(errors),
              toastType: ToastType.ERROR,
            });
          } else {
            newToastNotification({
              body: 'Import is successfully started!',
              toastType: ToastType.SUCCESS,
            });
          }
        } catch {
          /* */
        }
      },
    }),

    updateCanixMappedLocation: builder.mutation({
      query: ({ vendorId, ...updates }) => ({
        url: CANIX_MAPPED_LOCATIONS_URL.replace(':vendorId', vendorId),
        method: 'PATCH',
        body: { ...camelToSnake(updates) },
      }),
      onQueryStarted: async ({ vendorId }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const { locationMapping, errors = [] } = camelize(data);

          if (errors.length) {
            newToastNotification({
              body: joinArray(errors),
              toastType: ToastType.ERROR,
            });
          } else {
            newToastNotification({
              body: 'Facility is successfully updated!',
              toastType: ToastType.SUCCESS,
            });
            dispatch(
              integrationsApi.util.updateQueryData(
                'getCanixMappedLocations',
                { vendorId },
                (draft) => Object.assign(draft, { locationMapping })
              )
            );
          }
        } catch {
          /* */
        }
      },
    }),

    updatePlaidLocation: builder.mutation({
      invalidatesTags: ['PlaidLocations'],
      query: ({ id, organizationType, organizationTypeId, ...updates }) => ({
        body: { ...camelToSnake(updates) },
        method: 'PATCH',
        url: `${PLAID_LOCATIONS_URL.replace(':organizationType', organizationType).replace(':organizationTypeId', organizationTypeId)}${id}/`,
      }),
      onQueryStarted: async ({ queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {
          newToastNotification({
            body: error.error.data.error,
            toastType: ToastType.ERROR,
          });
        }
      },
    }),
  }),
});

export const {
  useAcceptLeafLinkTOSMutation,

  useCreatePlaidAccessTokenMutation,
  useCreatePlaidLinkTokenMutation,
  useRelinkPlaidAccessTokenMutation,
  useVerifyPlaidAccessTokenMutation,

  useFilterCanixSellableMutation,

  useGetCanixApiKeyQuery,
  useGetCanixBatchesQuery,
  useGetCanixFacilitiesQuery,
  useGetCanixLocationsQuery,
  useGetCanixMappedLocationsQuery,
  useGetPlaidAccessTokensQuery,
  useGetPlaidLocationsQuery,
  useGetResourcesQuery,
  useLazyGetResourcesQuery,

  useGetUserContextQuery,

  useGetImportResultsQuery,
  useLazyGetImportResultsQuery,

  useMapCanixLocationMutation,
  useSaveCanixApiKeyMutation,
  useStartCanixImportMutation,
  useUpdateCanixMappedLocationMutation,

  useUpdatePlaidLocationMutation,
} = integrationsApi;
